import axios from "axios";
import { Listing, Service } from "../types";
import serialiseService from "./serialiseService";

export type SuccessfulSearchResult = {
  _status: {
    errors?: [];
  };
  _embedded: {
    healthcareServices: Array<Service>;
  };
};

export type NoServicesFound = {
  _status: {
    errors: Array<{
      type: string;
      code: string;
      message: string;
      developerMessage: string;
    }>;
  };
};

export type SearchResult = SuccessfulSearchResult | NoServicesFound;

type ParamKeys = "latitude" | "longitude" | "name" | "postcode";
export type SearchParams = Partial<Record<ParamKeys, string>>;

function serialiseResponse(
  response: SuccessfulSearchResult["_embedded"]["healthcareServices"]
) {
  return response
    .map(serialiseService)
    .filter((listing) => !!listing.displayName);
}

const proximityParams = (lat: string, long: string) => {
  return {
    "location.proximity.near": `${lat},${long}`,
    "location.proximity.near_distance": "500000",
  };
};

const postcodeParams = (postcode: string) => {
  return {
    "location.proximity.near_postcode": postcode,
  };
};

const nameParams = (name: string) => {
  return {
    "search.name": name,
  };
};

const snomedParams = () => {
  const optometristSNOMEDcode = "310105000";
  const serviceType = `nhsd:/reference/taxonomies/snomed-servicetype/${optometristSNOMEDcode}`;

  return { "filter.serviceType.codes": serviceType };
};

export const constructParams = (params: SearchParams) => {
  let combinedParams = snomedParams();

  const { latitude, longitude, name, postcode } = params;

  if (latitude && longitude) {
    combinedParams = {
      ...combinedParams,
      ...proximityParams(latitude, longitude),
    };
  }

  if (name) {
    combinedParams = { ...combinedParams, ...nameParams(name) };
  }

  if (postcode) {
    combinedParams = { ...combinedParams, ...postcodeParams(postcode) };
  }

  return combinedParams;
};

const nhsdDirectorySearch = async (
  params: SearchParams
): Promise<Array<Listing>> => {
  const response = await axios
    .get<SearchResult>(
      "https://api.nhsd.healthdirect.org.au/v5/healthcareServices/_search",
      {
        headers: {
          "x-api-key": "9zNfxJzo2S90RMm2UIBFj4mZe3Fc3m6w6QsgAEoP",
        },
        params: constructParams(params),
      }
    )
    .then((response) => {
      if ("_embedded" in response.data) {
        return serialiseResponse(response.data._embedded.healthcareServices);
      }

      // TODO: Handle failure to find healthcare services
      // response.data._status.errors
      return [];
    })
    .catch((error) => {
      // Not currently showing error messages
      return [];
    });

  return response as Array<Listing>;
};

export {
  nhsdDirectorySearch as default,
  proximityParams,
  snomedParams,
  nameParams,
  postcodeParams,
};
